import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
  QueryFunctionContext,
  UseSuspenseQueryOptions,
} from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { match } from 'ts-pattern';
import { DateRange } from 'react-day-picker';

import API from '@/api';
import { IDestination } from '@/states/destination/types';
import { IDataSourceAccounts } from '@/views/datasources/datasources';
import { useGetClients } from '@/hooks/api/client';
import { useInsights } from '@/states';

export type ProviderAccountLite = {
  clientId?: string;
  subAccount?: boolean;
  subAccounts?: ProviderAccountLite[];
};

const QUERY_KEY = {
  DESTINATIONS: () => ({
    queryKey: ['destinations'] as const,
    queryFn: async ({ signal }: QueryFunctionContext) => {
      const destinations = (await API.destinations.get(signal)) as Awaited<
        IDestination[]
      >;
      if (destinations.length) return destinations[0];
      return null;
    },
  }),
  DATA_SOURCES: (businessId: string) => ({
    queryKey: ['data-sources', businessId] as const,
    queryFn: async ({ signal }: QueryFunctionContext) => {
      const params = {
        businessId,
        include: ['provider'],
        paginate: false,
      };

      const response = (await API.accounts.find(
        params,
        signal
      )) as IDataSourceAccounts<any>[];

      if (!response) return [];

      const accounts = response.map(account => {
        if (
          /googleAnalytics|googleAds/gi.test(account.provider.type) &&
          account.subAccounts?.length &&
          account.subAccounts.length > 0
        ) {
          const subAccounts = account.subAccounts.map(subAcc => ({
            ...subAcc,
            status: 'valid' as const,
            service: account.provider.type,
          }));
          return {
            ...account,
            service: account.provider.type,
            subAccounts,
          };
        }

        return {
          ...account,
          service: account.provider.type,
        };
      });

      return accounts;
    },
    staleTime: 5 * 60 * 1000,
  }),
};

/**
 * DATA SOURCES
 */
const useDataSources = <TData = IDataSourceAccounts<any>[]>(
  businessId: string,
  options?: Omit<
    UseSuspenseQueryOptions<
      IDataSourceAccounts<any>[],
      Error,
      TData,
      ReturnType<typeof QUERY_KEY.DATA_SOURCES>['queryKey']
    >,
    'queryKey' | 'queryFn'
  >
) => {
  const { queryKey, queryFn } = QUERY_KEY.DATA_SOURCES(businessId);
  return useSuspenseQuery({
    ...options,
    queryKey,
    queryFn,
  });
};

const useDataSourceDetail = ({
  businessId,
  datasourceId,
  isSubAccount,
}: {
  businessId: string;
  datasourceId: string;
  isSubAccount: boolean;
}) =>
  useDataSources(businessId, {
    select: accounts =>
      accounts.find(
        account =>
          account.id === datasourceId &&
          isSubAccount === Boolean(account.subAccount)
      ),
  });

const useClientDataSources = ({
  businessId,
  clientId,
}: {
  businessId: string;
  clientId: string;
}) =>
  useDataSources(businessId, {
    refetchOnMount: false,
    select: accounts =>
      accounts.filter(
        account =>
          account.clientId != null &&
          (account.clientId?.toString() ?? '') === (clientId?.toString() ?? '')
      ),
  });

const useInvalidateDataSources = () => {
  const queryClient = useQueryClient();
  return useCallback(
    async (businessId: string) => {
      const { queryKey } = QUERY_KEY.DATA_SOURCES(businessId);
      await queryClient.invalidateQueries({
        queryKey,
      });
    },
    [queryClient]
  );
};

const useDisconnectDataSource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      accountIds,
      businessId,
    }: {
      accountIds: Array<{ subAccount: boolean; accountId: string }>;
      businessId: string;
    }) => {
      const { queryKey, queryFn } = QUERY_KEY.DATA_SOURCES(businessId);

      const accounts = await queryClient.ensureQueryData({ queryKey, queryFn });

      const accountsById = accounts.reduce<
        Record<
          string,
          Record<'subAccount' | 'account', IDataSourceAccounts<any> | null>
        >
      >((acc, curr) => {
        if (!acc[curr.id]) {
          acc[curr.id] = {
            subAccount: null,
            account: null,
          };
        }

        acc[curr.id][curr.subAccount ? 'subAccount' : 'account'] = curr;

        return acc;
      }, {});

      const tasks = accountIds.map(({ accountId, subAccount }) => {
        const account =
          accountsById[accountId][subAccount ? 'subAccount' : 'account'];

        if (!account) {
          throw new Error('Disconnect failed, account not found');
        }

        if (subAccount && account?.subAccount) {
          return API.subAccounts.update(accountId, {
            clientId: null,
            businessId,
          });
        }

        return API.accounts.update(
          accountId,
          {
            clientId: null,
            businessId,
          },
          {
            fields: 'provider',
          }
        );
      });

      const updatedAccounts = await Promise.all(tasks.filter(Boolean));

      return updatedAccounts;
    },
    onSettled: async (data, error, variables, context) => {
      const { queryKey } = QUERY_KEY.DATA_SOURCES(variables.businessId);
      await queryClient.invalidateQueries({
        queryKey,
      });
    },
  });
};

const useRenameDataSource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      accountId,
      isSubAccount,
      businessId,
      newName,
    }: {
      accountId: string;
      isSubAccount: boolean;
      businessId: string;
      newName: string;
    }) => {
      const { queryKey } = QUERY_KEY.DATA_SOURCES(businessId);

      const accounts =
        queryClient.getQueryData<IDataSourceAccounts<any>[]>(queryKey) ?? [];

      const account = accounts.find(
        acc => acc.id === accountId && isSubAccount === Boolean(acc.subAccount)
      );

      if (!account) return null;

      // sub account
      if (account?.subAccount) {
        return API.subAccounts.update(accountId, {
          businessId,
          label: newName,
        });
      }

      return API.accounts.update(
        accountId,
        {
          businessId,
          label: newName,
        },
        {
          fields: 'provider',
        }
      );
    },
    onSettled: async (data, error, variables, context) => {
      const { queryKey } = QUERY_KEY.DATA_SOURCES(variables.businessId);
      await queryClient.invalidateQueries({
        queryKey,
      });
    },
  });
};

const useRefreshDataSource = () => {
  const queryClient = useQueryClient();
  const getClients = useGetClients();
  return useMutation({
    mutationFn: async ({
      businessId,
      clientId,
      dateFilter,
      datasourceId,
    }: {
      clientId: string;
      businessId: string;
      dateFilter: DateRange | undefined;
      datasourceId: string;
    }) => {
      if (clientId && dateFilter?.from && dateFilter.to) {
        // Only invalidate the query - React Query will handle the refetch automatically
        await queryClient.invalidateQueries({
          queryKey: [
            'insights',
            {
              businessId,
              clientId,
              from: dateFilter.from.toISOString(),
              to: dateFilter.to.toISOString(),
              selectedAccountId: datasourceId,
            },
          ],
        });
      }
    },
    onSettled: async (_, __, variables) => {
      if (variables.businessId) {
        await getClients((variables.businessId as unknown) as null, true);
        const { queryKey } = QUERY_KEY.DATA_SOURCES(variables.businessId);
        await queryClient.invalidateQueries({
          queryKey,
        });
      }
    },
  });
};

/**
 * DESTINATIONS
 */
const useDestinations = () => {
  const { queryKey, queryFn } = QUERY_KEY.DESTINATIONS();
  return useSuspenseQuery({
    queryKey,
    queryFn,
  });
};

const useDisconnectDestination = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (destinationId: string) => {
      return API.destinations.delete(destinationId);
    },
    onSettled: async (data, error, variables, context) => {
      const { queryKey } = QUERY_KEY.DESTINATIONS();
      await queryClient.invalidateQueries({
        queryKey,
      });
    },
  });
};

const useInvalidateDestinations = () => {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    const { queryKey } = QUERY_KEY.DESTINATIONS();
    await queryClient.invalidateQueries({ queryKey });
  }, [queryClient]);
};

export const ConnectionQuery = {
  QUERY_KEY,
  // DATA SOURCES
  useInvalidateDataSources,
  useDisconnectDataSource,
  useRefreshDataSource,
  useRenameDataSource,
  useClientDataSources,
  useDataSourceDetail,
  useDataSources,
  // DESINATIONS
  useDestinations,
  useDisconnectDestination,
  useInvalidateDestinations,
};

export const isConnected = (acc: ProviderAccountLite): boolean =>
  acc.clientId != null;

export const isSubAccount = (acc: ProviderAccountLite): boolean =>
  !!acc.subAccount;

/**
 * If the account has sub accounts, and all of them are connected, then dont show it (usually it's an "All" account)
 * @param acc
 * @returns
 */
export const isAllSubAccountConnected = (acc: ProviderAccountLite): boolean =>
  !!acc.subAccounts?.length && acc.subAccounts.every(sub => isConnected(sub));
